import { useEffect, useState, useCallback, useRef } from 'react'
import { api } from '../../lib/api'
import { useToast } from '../ui/Toast'
import { Button } from '../ui/Button'
import { EmptyState } from '../ui/EmptyState'
import { DeviceActionButtons, ActiveSessionsList, useSessionStart, GlobeIcon } from './SessionLauncher'
import type { SiteDeviceWithSpec } from '@shared/types'

interface CustomPort {
  port: number
  label?: string
}

interface DiscoveredDevice {
  id: string
  ip_address: string
  mac_address: string | null
  hostname: string | null
  open_ports: string | null
  custom_ports: string | null
  device_type: string | null
  manufacturer: string | null
  http_title: string | null
  status: string
  online: number
  latency_ms: number | null
  last_seen: string
  linked_device_id: string | null
}

interface Props {
  siteId: string
  agentConnected?: boolean
}

function SearchIcon({ size = 16, style }: { size?: number; style?: React.CSSProperties }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={style}><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
}

type FilterTab = 'new' | 'linked' | 'ignored'

export function DiscoveredDevicesPanel({ siteId, agentConnected: agentConnectedProp }: Props) {
  const [devices, setDevices] = useState<DiscoveredDevice[]>([])
  const [siteDevices, setSiteDevices] = useState<SiteDeviceWithSpec[]>([])
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState<FilterTab>('new')
  const [linkModal, setLinkModal] = useState<DiscoveredDevice | null>(null)
  const [agentOnline, setAgentOnline] = useState(agentConnectedProp ?? false)
  const { toast } = useToast()
  const { sessions, startingId, startSession, endSession } = useSessionStart(siteId, agentOnline)

  const load = useCallback(async () => {
    try {
      const [discovered, existing, agentStatus] = await Promise.all([
        api.get<{ data: DiscoveredDevice[] }>(`/sites/${siteId}/discovered-devices`),
        api.get<{ data: SiteDeviceWithSpec[] }>(`/sites/${siteId}/devices?limit=200`),
        agentConnectedProp === undefined
          ? api.get<{ configured: boolean; agent?: { status: string; last_heartbeat: string | null } }>(`/sites/${siteId}/agent/status`).catch(() => null)
          : null,
      ])
      setDevices(discovered.data || [])
      setSiteDevices(existing.data || [])
      if (agentStatus && agentStatus.configured && agentStatus.agent) {
        const hb = agentStatus.agent.last_heartbeat
        setAgentOnline(
          agentStatus.agent.status === 'active' && !!hb && (Date.now() - new Date(hb).getTime()) < 120000
        )
      }
    } catch { /* ignore */ }
    setLoading(false)
  }, [siteId, agentConnectedProp])

  useEffect(() => { load() }, [load])

  const filtered = devices.filter(d => d.status === tab)
  const counts = {
    new: devices.filter(d => d.status === 'new').length,
    linked: devices.filter(d => d.status === 'linked').length,
    ignored: devices.filter(d => d.status === 'ignored').length,
  }

  const ignoreDevice = async (id: string) => {
    try {
      await api.post(`/sites/${siteId}/discovered-devices/${id}/ignore`)
      toast('Device ignored', 'success')
      load()
    } catch {
      toast('Failed to ignore device', 'error')
    }
  }

  const linkDevice = async (discoveredId: string, siteDeviceId: string) => {
    try {
      await api.post(`/sites/${siteId}/discovered-devices/${discoveredId}/link`, { site_device_id: siteDeviceId })
      toast('Device linked', 'success')
      setLinkModal(null)
      load()
    } catch {
      toast('Failed to link device', 'error')
    }
  }

  const [addingPortFor, setAddingPortFor] = useState<string | null>(null)
  const [newPort, setNewPort] = useState('')
  const [newPortLabel, setNewPortLabel] = useState('')
  const portInputRef = useRef<HTMLInputElement>(null)

  const parsePorts = (portsStr: string | null): number[] => {
    if (!portsStr) return []
    try {
      return JSON.parse(portsStr)
    } catch {
      return []
    }
  }

  const parseCustomPorts = (portsStr: string | null): CustomPort[] => {
    if (!portsStr) return []
    try {
      return JSON.parse(portsStr)
    } catch {
      return []
    }
  }

  const saveCustomPort = async (device: DiscoveredDevice) => {
    const portNum = parseInt(newPort, 10)
    if (!portNum || portNum < 1 || portNum > 65535) {
      toast('Enter a valid port (1-65535)', 'error')
      return
    }
    const existing = parseCustomPorts(device.custom_ports)
    if (existing.some(p => p.port === portNum) || parsePorts(device.open_ports).includes(portNum)) {
      toast('Port already exists', 'error')
      return
    }
    const updated = [...existing, { port: portNum, label: newPortLabel.trim() || undefined }]
    try {
      await api.patch(`/sites/${siteId}/discovered-devices/${device.id}/custom-ports`, { custom_ports: updated })
      toast(`Port ${portNum} added`, 'success')
      setAddingPortFor(null)
      setNewPort('')
      setNewPortLabel('')
      load()
    } catch {
      toast('Failed to save custom port', 'error')
    }
  }

  const removeCustomPort = async (device: DiscoveredDevice, portToRemove: number) => {
    const existing = parseCustomPorts(device.custom_ports)
    const updated = existing.filter(p => p.port !== portToRemove)
    try {
      await api.patch(`/sites/${siteId}/discovered-devices/${device.id}/custom-ports`, { custom_ports: updated })
      toast(`Port ${portToRemove} removed`, 'success')
      load()
    } catch {
      toast('Failed to remove port', 'error')
    }
  }

  if (loading) {
    return <div style={{ padding: 24, color: 'var(--text-3)' }}>Loading discovered devices...</div>
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Tab Filters */}
      <div style={{ display: 'flex', gap: 4 }}>
        {(['new', 'linked', 'ignored'] as FilterTab[]).map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              padding: '6px 14px', borderRadius: 'var(--r-m)',
              border: tab === t ? '1px solid var(--accent)' : '1px solid var(--border)',
              background: tab === t ? 'var(--accent-m)' : 'var(--bg-1)',
              color: tab === t ? 'var(--accent)' : 'var(--text-2)',
              fontSize: 12, fontWeight: 600, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 6,
              textTransform: 'capitalize',
            }}
          >
            {t}
            <span style={{
              padding: '1px 6px', borderRadius: 'var(--r-f)', fontSize: 10, fontWeight: 700,
              background: tab === t ? 'var(--accent)' : 'var(--bg-2)',
              color: tab === t ? 'var(--bg-0)' : 'var(--text-3)',
            }}>
              {counts[t]}
            </span>
          </button>
        ))}
      </div>

      {/* Devices Table */}
      {filtered.length === 0 ? (
        <EmptyState
          icon={SearchIcon}
          title={`No ${tab} devices`}
          description={tab === 'new' ? 'Run a device scan to discover devices on this network.' : `No devices are currently ${tab}.`}
        />
      ) : (
        <div style={{
          background: 'var(--bg-1)', borderRadius: 'var(--r-l)',
          border: '1px solid var(--border)', overflow: 'hidden',
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                <th style={thStyle}>IP</th>
                <th style={thStyle}>MAC / Vendor</th>
                <th style={thStyle}>Ports</th>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>Online</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(device => {
                const ports = parsePorts(device.open_ports)
                const customPorts = parseCustomPorts(device.custom_ports)
                const allPorts = [...ports, ...customPorts.map(cp => cp.port)]
                const hasActions = allPorts.some(p => [22, 80, 443, 3389, 8080, 8443].includes(p)) || customPorts.length > 0
                const isAddingPort = addingPortFor === device.id
                return (
                  <tr key={device.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ ...tdStyle, fontFamily: 'var(--mono)', fontSize: 12 }}>
                      {device.ip_address}
                      {device.hostname && (
                        <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{device.hostname}</div>
                      )}
                    </td>
                    <td style={tdStyle}>
                      {device.mac_address ? (
                        <span style={{ fontFamily: 'var(--mono)', fontSize: 11 }}>{device.mac_address}</span>
                      ) : '—'}
                      {device.manufacturer && (
                        <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{device.manufacturer}</div>
                      )}
                    </td>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                        {ports.map(p => (
                          <span key={p} style={{
                            padding: '1px 6px', borderRadius: 'var(--r-f)', fontSize: 10, fontWeight: 600,
                            background: 'var(--bg-2)', fontFamily: 'var(--mono)',
                          }}>
                            {p}
                          </span>
                        ))}
                        {customPorts.map(cp => (
                          <span
                            key={`custom-${cp.port}`}
                            title={cp.label ? `${cp.label} (custom) — click to remove` : `Custom port — click to remove`}
                            onClick={() => removeCustomPort(device, cp.port)}
                            style={{
                              padding: '1px 6px', borderRadius: 'var(--r-f)', fontSize: 10, fontWeight: 600,
                              background: 'var(--accent-m)', color: 'var(--accent)',
                              fontFamily: 'var(--mono)', cursor: 'pointer',
                              border: '1px solid var(--accent)',
                            }}
                          >
                            {cp.port}{cp.label ? ` ${cp.label}` : ''}
                          </span>
                        ))}
                        {/* Add port button */}
                        {!isAddingPort ? (
                          <button
                            onClick={() => { setAddingPortFor(device.id); setTimeout(() => portInputRef.current?.focus(), 50) }}
                            title="Add custom port"
                            style={{
                              width: 20, height: 20, borderRadius: 'var(--r-f)',
                              border: '1px dashed var(--border)', background: 'none',
                              fontSize: 13, color: 'var(--text-3)', cursor: 'pointer',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              lineHeight: 1, padding: 0,
                            }}
                          >
                            +
                          </button>
                        ) : (
                          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                            <input
                              ref={portInputRef}
                              type="number"
                              placeholder="Port"
                              value={newPort}
                              onChange={e => setNewPort(e.target.value)}
                              onKeyDown={e => { if (e.key === 'Enter') saveCustomPort(device); if (e.key === 'Escape') setAddingPortFor(null) }}
                              style={{
                                width: 60, padding: '2px 6px', borderRadius: 'var(--r-f)',
                                border: '1px solid var(--border)', background: 'var(--bg-2)',
                                fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--text-1)',
                                outline: 'none',
                              }}
                            />
                            <input
                              type="text"
                              placeholder="Label"
                              value={newPortLabel}
                              onChange={e => setNewPortLabel(e.target.value)}
                              onKeyDown={e => { if (e.key === 'Enter') saveCustomPort(device); if (e.key === 'Escape') setAddingPortFor(null) }}
                              style={{
                                width: 80, padding: '2px 6px', borderRadius: 'var(--r-f)',
                                border: '1px solid var(--border)', background: 'var(--bg-2)',
                                fontSize: 11, color: 'var(--text-1)', outline: 'none',
                              }}
                            />
                            <button
                              onClick={() => saveCustomPort(device)}
                              style={{
                                padding: '2px 8px', borderRadius: 'var(--r-f)',
                                background: 'var(--accent)', color: 'var(--bg-0)',
                                border: 'none', fontSize: 11, fontWeight: 600, cursor: 'pointer',
                              }}
                            >
                              Add
                            </button>
                            <button
                              onClick={() => { setAddingPortFor(null); setNewPort(''); setNewPortLabel('') }}
                              style={{
                                padding: '2px 6px', borderRadius: 'var(--r-f)',
                                background: 'none', border: '1px solid var(--border)',
                                fontSize: 11, color: 'var(--text-3)', cursor: 'pointer',
                              }}
                            >
                              ✕
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                    <td style={tdStyle}>
                      {device.device_type && (
                        <span style={{
                          padding: '2px 8px', borderRadius: 'var(--r-f)', fontSize: 11, fontWeight: 600,
                          background: 'var(--bg-2)', textTransform: 'capitalize',
                        }}>
                          {device.device_type}
                        </span>
                      )}
                    </td>
                    <td style={tdStyle}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12,
                      }}>
                        <span style={{
                          width: 7, height: 7, borderRadius: '50%',
                          background: device.online ? 'var(--ok)' : 'var(--err)',
                        }} />
                        {device.online ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end', flexWrap: 'wrap', alignItems: 'center' }}>
                        {/* Session buttons (based on auto-detected ports) */}
                        {hasActions && (
                          <DeviceActionButtons
                            ip={device.ip_address}
                            ports={allPorts}
                            agentConnected={agentOnline}
                            startingId={startingId}
                            onOpen={(port) => startSession(device.ip_address, port, 'tcp')}
                            onSSH={() => startSession(device.ip_address, 22, 'ssh')}
                            onRDP={() => startSession(device.ip_address, 3389, 'rdp')}
                          />
                        )}
                        {/* Open buttons for custom ports not covered by DeviceActionButtons */}
                        {customPorts
                          .filter(cp => ![80, 443, 8080, 8443, 22, 3389].includes(cp.port))
                          .map(cp => (
                            <Button
                              key={`open-${cp.port}`}
                              size="sm" variant="secondary" icon={<GlobeIcon />}
                              disabled={!agentOnline}
                              onClick={() => startSession(device.ip_address, cp.port, 'tcp')}
                              title={cp.label || `Port ${cp.port}`}
                            >
                              {cp.label || cp.port}
                            </Button>
                          ))}
                        {/* Management buttons */}
                        {tab === 'new' && (
                          <>
                            <Button size="sm" variant="ghost" onClick={() => setLinkModal(device)}>Link</Button>
                            <Button size="sm" variant="ghost" onClick={() => ignoreDevice(device.id)}>Ignore</Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Active Sessions */}
      {sessions.length > 0 && (
        <ActiveSessionsList sessions={sessions} onEnd={endSession} />
      )}

      {/* Link Modal */}
      {linkModal && (
        <LinkDeviceModal
          device={linkModal}
          siteDevices={siteDevices}
          onLink={(siteDeviceId) => linkDevice(linkModal.id, siteDeviceId)}
          onClose={() => setLinkModal(null)}
        />
      )}
    </div>
  )
}

function LinkDeviceModal({ device, siteDevices, onLink, onClose }: {
  device: DiscoveredDevice
  siteDevices: SiteDeviceWithSpec[]
  onLink: (siteDeviceId: string) => void
  onClose: () => void
}) {
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
    }} onClick={onClose}>
      <div
        style={{
          background: 'var(--bg-1)', borderRadius: 'var(--r-xl)', border: '1px solid var(--border)',
          width: 400, maxWidth: '90vw', padding: 24, boxShadow: 'var(--sh-lg)',
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Link Device</div>
        <p style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 16 }}>
          Link <strong style={{ fontFamily: 'var(--mono)' }}>{device.ip_address}</strong> to an existing site device.
        </p>

        <div style={{ maxHeight: 300, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {siteDevices.map(sd => (
            <button
              key={sd.id}
              onClick={() => onLink(sd.id)}
              style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '10px 12px', borderRadius: 'var(--r-m)',
                border: '1px solid var(--border)', background: 'var(--bg-1)',
                cursor: 'pointer', textAlign: 'left', width: '100%',
              }}
            >
              <div>
                <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-1)' }}>{sd.label || 'Unnamed'}</div>
                <div style={{ fontSize: 11, color: 'var(--text-3)', fontFamily: 'var(--mono)' }}>
                  {sd.ip_address || 'No IP'}
                </div>
              </div>
              <span style={{ fontSize: 11, color: 'var(--text-3)' }}>{sd.spec_category || ''}</span>
            </button>
          ))}
          {siteDevices.length === 0 && (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>
              No existing devices to link to.
            </div>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
        </div>
      </div>
    </div>
  )
}

const thStyle: React.CSSProperties = {
  textAlign: 'left', padding: '8px 16px', fontSize: 11,
  fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase',
  letterSpacing: '0.05em',
}

const tdStyle: React.CSSProperties = {
  padding: '10px 16px',
}
